import React from 'react';
import { Table, ConfigProvider,Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { CircleHelp } from 'lucide-react';
import { mixformatCurrency } from 'services/mixin';

interface ReportItem {
    id: string;
    customerName: string;
    postingDate?: string;
    sourceDocument?: string;
    creditAmount?: number;
    debitAmount?: number;
    remainingAmount?: number;
    link?: string;
    isHeader?: boolean;
    children?: ReportItem[];
}

const reportData: ReportItem[] = [
    {
        id: 'c1',
        customerName: '1. Công Ty Cổ Phần Kinh Doanh Và Quản Lý Bất Động Sản Vinhomes',
        remainingAmount: 0,
        isHeader: true,
        link: '/debt-detail/detail',
        children: [
            {
                id: 'c1-1',
                customerName: '082026/HĐMB/30',
                postingDate: '18/08/2026',
                sourceDocument: 'Hợp đồng bán',
                creditAmount: undefined,
                debitAmount: undefined,
                remainingAmount: undefined,
            },
            {
                id: 'c1-2',
                customerName: '082026/HĐMB/31',
                postingDate: '19/08/2026',
                sourceDocument: 'Hợp đồng bán',
                creditAmount: 5000000,
                debitAmount: 0,
                remainingAmount: 5000000,
            },
            {
                id: 'c1-3',
                customerName: '082026/HĐMB/32',
                postingDate: '20/08/2026',
                sourceDocument: 'Hợp đồng bán',
                creditAmount: 0,
                debitAmount: 5000000,
                remainingAmount: -5000000,
            },
        ],
    },
    {
        id: 'c2',
        customerName: '2. Nguyễn Thị Huệ',
        debitAmount: 6500000,
        remainingAmount: -6500000,
        link: '/debt-detail/detail',
        isHeader: true,
        children: [
            {
                id: 'c2-1',
                customerName: 'PXK__00072',
                postingDate: '06/08/2026',
                sourceDocument: 'Phiếu xuất kho',
                debitAmount: 6500000,
                remainingAmount: -6500000,
            },
            {
                id: 'c2-2',
                customerName: 'HĐB__00102',
                postingDate: '10/08/2026',
                sourceDocument: 'Hợp đồng bán',
                creditAmount: 2000000,
                remainingAmount: 2000000,
            },
            {
                id: 'c2-3',
                customerName: 'HĐB__00103',
                postingDate: '12/08/2026',
                sourceDocument: 'Hợp đồng bán',
                creditAmount: 1000000,
                remainingAmount: 1000000,
            },
        ],
    },
];

export default function TableReport() {

    const columns: ColumnsType<ReportItem> = [
        {
            title: 'Khách hàng',
            dataIndex: 'customerName',
            key: 'customerName',
            width: 400,
            render: (text, record) => (
                record.link ? (
                    <Link
                        to={record.link}
                        className={ record.isHeader 
                            ? "font-bold text-gray-900 dark:text-gray-100"
                            : "pl-4 text-gray-700 dark:text-gray-300"
                        }
                    >
                        {text}
                    </Link>
                ) : (
                    <span
                        className={ record.isHeader
                            ? "font-bold text-gray-900 dark:text-gray-100"
                            : "pl-4 text-gray-700 dark:text-gray-300"
                        }
                    >
                        {text}
                    </span>
                )

            ),
        },
        {
            title: 'Ngày hạch toán',
            dataIndex: 'postingDate',
            key: 'postingDate',
            align: 'center',
            width: 150,
            render: (text) => text || '--',
        },
        {
            title: 'Nguồn chứng từ',
            dataIndex: 'sourceDocument',
            key: 'sourceDocument',
            width: 180,
            render: (text) => text || '--',
        },
        {
           title: (
                <div className="flex items-center gap-1.5">
                    <span>Dư có</span>
                    <Tooltip title="Là các khoản mà khách hàng có gồm: Nhập tiền thủ công công nợ, từ đơn hàng bán, hợp đồng bán">
                        <CircleHelp size={12} className="text-orange-400 cursor-pointer" />
                    </Tooltip>
                </div>
            ),
            dataIndex: 'creditAmount',
            key: 'creditAmount',
            align: 'right',
            width: 150,
            render: (val) => mixformatCurrency(val,false),
        },
        {
             title: (
                <div className="flex items-center gap-1.5">
                    <span>Dư nợ</span>
                    <Tooltip title="Là các khoản mà khách hàng đã trả: Gồm từ phiếu xuất kho, hoặc nhập số tiền âm từ công nợ">
                        <CircleHelp size={12} className="text-orange-400 cursor-pointer" />
                    </Tooltip>
                </div>
            ),
            dataIndex: 'debitAmount',
            key: 'debitAmount',
            align: 'right',
            width: 150,
            render: (val, record) => (
                <span className={record.isHeader && val ? 'font-bold' : ''}>
                    {mixformatCurrency(val,false)}
                </span>
            ),
        },
        {
            title: 'Còn lại',
            dataIndex: 'remainingAmount',
            key: 'remainingAmount',
            align: 'right',
            width: 150,
            render: (val, record) => (
                <span className={record.isHeader ? 'font-bold' : ''}>
                    {mixformatCurrency(val,false)}
                </span>
            ),
        },
    ];

    return (
        <ConfigProvider
            theme={{
                components: {
                    Table: {
                        headerBg: '#ffffff',
                        headerColor: '#6b7280',
                        cellPaddingBlock: 10,
                        rowHoverBg: '#f9fafb',
                    },
                },
            }}
        >
            <Table<ReportItem>
                columns={columns}
                dataSource={reportData}
                rowKey="id"
                pagination={false}
                expandable={{
                    defaultExpandAllRows: true,
                    showExpandColumn: false, // Ẩn nút expand để hiển thị phẳng giống như UI ảnh gốc
                }}
                summary={() => (
                    <Table.Summary fixed>
                        <Table.Summary.Row className="font-bold bg-white border-t">
                            <Table.Summary.Cell index={0}>Tổng</Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="center">--</Table.Summary.Cell>
                            <Table.Summary.Cell index={2}>--</Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">0</Table.Summary.Cell>
                            <Table.Summary.Cell index={4} align="right">6.500.000</Table.Summary.Cell>
                            <Table.Summary.Cell index={5} align="right">-6.500.000</Table.Summary.Cell>
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />
        </ConfigProvider>
    );
}